import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/access";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Settings2, Palette, LogIn, Check, RotateCcw, Building2, ImagePlus, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { BASES, ACCENTS, swatch, applyTheme } from "@/lib/theme";
import { PhotoField, UserAvatar } from "@/components/UserAvatar";
import { BrandText, refreshBrand } from "@/lib/brand";

function ThemePicker({ value, onChange, testId }) {
  return (
    <div className="space-y-4" data-testid={testId}>
      <div>
        <Label className="text-xs text-slate-400 uppercase font-mono">Zemin</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
          {Object.entries(BASES).map(([k, b]) => (
            <button key={k} type="button" onClick={() => onChange({ ...value, base: k })} data-testid={`${testId}-base-${k}`}
                    className={`relative rounded-xl border p-2 text-left transition-colors ${value.base === k ? "border-blue-500 ring-2 ring-blue-500/40" : "border-slate-700 hover:border-slate-500"}`}>
              <div className="flex h-8 rounded-lg overflow-hidden">{[950, 900, 800, 600, 400].map((s) => <span key={s} className="flex-1" style={{ background: swatch(b, s) }} />)}</div>
              <div className="text-xs font-semibold mt-1.5">{b.label}</div>
              {value.base === k && <Check size={14} className="absolute top-1 right-1 text-blue-300" />}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-400 uppercase font-mono">Vurgu Rengi</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {Object.entries(ACCENTS).map(([k, a]) => (
            <button key={k} type="button" onClick={() => onChange({ ...value, accent: k })} data-testid={`${testId}-accent-${k}`}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${value.accent === k ? "border-white/70 bg-white/10" : "border-slate-700 hover:border-slate-500"}`}>
              <span className="h-4 w-4 rounded-full" style={{ background: swatch(a, 500) }} /> {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function BranchThemeRow({ b, theme, company, onSaved }) {
  const [v, setV] = useState(theme || company);
  const [open, setOpen] = useState(false);
  const save = async () => {
    try { await http.put(`/theme/branch/${b.id}`, v); toast.success(`${b.name} teması kaydedildi`); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  const reset = async () => {
    try { await http.delete(`/theme/branch/${b.id}`); toast.success("Şirket temasına dönüldü"); setV(company); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sıfırlanamadı"); }
  };
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass p-4" data-testid={`branch-theme-${b.id}`}>
      <div className="flex items-center gap-3">
        <Building2 size={16} className="text-blue-300" />
        <div className="flex-1 font-semibold">{b.name}</div>
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${theme ? "bg-blue-500/15 text-blue-200" : "bg-slate-800 text-slate-400"}`}>{theme ? "ÖZEL TEMA" : "ŞİRKET TEMASI"}</span>
        <Button size="sm" variant="outline" className="border-slate-700" onClick={() => setOpen(!open)} data-testid={`branch-theme-toggle-${b.id}`}>{open ? "Kapat" : "Düzenle"}</Button>
      </div>
      {open && (
        <div className="mt-4 space-y-4">
          <ThemePicker value={v} onChange={setV} testId={`branch-theme-picker-${b.id}`} />
          <div className="flex gap-2 justify-end">
            {theme && <Button variant="outline" className="border-slate-700" onClick={reset} data-testid={`branch-theme-reset-${b.id}`}><RotateCcw size={14} className="mr-1" /> Şirket Temasına Dön</Button>}
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-500" data-testid={`branch-theme-save-${b.id}`}>Kaydet</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function ThemeTab() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [company, setCompany] = useState(null);
  const load = () => http.get("/theme/all").then((r) => { setData(r.data); setCompany((c) => c || r.data.company); });
  useEffect(() => {
    load(); http.get("/branches").then((r) => setBranches(r.data));
    return () => { http.get("/theme").then((r) => applyTheme(r.data.effective)).catch(() => {}); };
  }, []);
  if (!data || !company) return <div className="text-slate-400">Yükleniyor...</div>;
  const mine = data.can_company ? branches : branches.filter((b) => b.id === user.branch_id);
  const preview = (t) => { setCompany(t); applyTheme(t); };
  const saveCompany = async () => {
    try { await http.put("/theme/company", company); toast.success("Şirket teması kaydedildi"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  return (
    <div className="space-y-6">
      {data.can_company && (
        <Card className="border-slate-800 bg-slate-900/60 glass p-5 space-y-4" data-testid="company-theme">
          <div className="flex items-center gap-2 font-display font-bold text-lg"><Palette size={18} className="text-blue-300" /> Şirket Teması</div>
          <p className="text-xs text-slate-400">Tüm kullanıcılara ve giriş ekranına uygulanır. Özel teması olan şubeler kendi temasını kullanır. Seçtiğiniz tema anında önizlenir.</p>
          <ThemePicker value={company} onChange={preview} testId="company-theme-picker" />
          <div className="flex justify-end"><Button onClick={saveCompany} className="bg-blue-600 hover:bg-blue-500" data-testid="company-theme-save">Şirket Temasını Kaydet</Button></div>
        </Card>
      )}
      <div className="space-y-3">
        <div className="text-xs font-mono uppercase text-slate-400">Şube Temaları</div>
        {mine.map((b) => <BranchThemeRow key={b.id} b={b} theme={data.branches[b.id]} company={data.company} onSaved={load} />)}
        {mine.length === 0 && <div className="text-sm text-slate-500">Düzenleyebileceğiniz şube yok.</div>}
      </div>
    </div>
  );
}

const BG_PRESETS = [["lines", "Işık Çizgileri"], ["waves", "Sinyal Dalgası"], ["grain", "Mat Siyah"], ["architecture", "Mimari"]];

function BgUpload({ onUploaded }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error("Dosya 20 MB'den büyük olamaz"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await http.post("/uploads?kind=login_bg", fd); onUploaded(data.storage_path); toast.success("Arka plan yüklendi"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="h-24 rounded-xl border-2 border-dashed border-slate-700 hover:border-blue-500 flex flex-col items-center justify-center gap-1 cursor-pointer text-xs text-slate-400" data-testid="login-bg-upload">
      <ImagePlus size={18} /> {busy ? "Yükleniyor..." : "Foto / GIF yükle"}
      <input type="file" accept="image/*,.gif" className="hidden" onChange={pick} data-testid="login-bg-upload-input" />
    </label>
  );
}

function DailyMessageCard() {
  const [v, setV] = useState(null);
  useEffect(() => { http.get("/settings/daily-message").then((r) => setV(r.data || {})); }, []);
  if (!v) return null;
  const save = async (text) => {
    try { const { data } = await http.put("/settings/daily-message", { text }); setV(data); refreshBrand(); toast.success(text ? "Günün mesajı yayında" : "Günün mesajı kaldırıldı"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  return (
    <Card className="border-amber-500/30 bg-amber-500/5 glass p-5 space-y-3" data-testid="daily-message-settings">
      <div className="font-display font-bold flex items-center gap-2"><Megaphone size={17} className="text-amber-300" /> Günün Mesajı</div>
      <p className="text-xs text-slate-400">Giriş ekranında giriş kutusunun üstünde görünür; değiştirilene veya kaldırılana kadar kalır.</p>
      <Textarea rows={2} maxLength={160} value={v.text || ""} onChange={(e) => setV({ ...v, text: e.target.value })}
                placeholder="Örn: Bugün hedefe 12 adet kaldı, hep birlikte!" className="bg-slate-950/50 border-slate-700" data-testid="daily-message-input" />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-slate-500">{(v.text || "").length}/160{v.by_name && v.at ? ` · son: ${v.by_name}, ${new Date(v.at).toLocaleString("tr-TR")}` : ""}</span>
        <div className="flex gap-2">
          {v.text && <Button variant="outline" className="border-slate-700" onClick={() => save("")} data-testid="daily-message-clear">Kaldır</Button>}
          <Button onClick={() => save(v.text || "")} className="bg-amber-600 hover:bg-amber-500" data-testid="daily-message-save">Yayınla</Button>
        </div>
      </div>
    </Card>
  );
}

function LoginTab() {
  const [v, setV] = useState(null);
  useEffect(() => {
    let alive = true;
    http.get("/settings/login").then((r) => { if (alive) setV((cur) => cur || { company_name: "", title: "", subtitle: "", logo_path: "", logo_preset: null, brand_text: "KARAHAN TEKNOLOJİ", bg_type: "none", bg_preset: null, bg_path: null, bg_dim: 55, ...r.data }); });
    return () => { alive = false; };
  }, []);
  if (!v) return <div className="text-slate-400">Yükleniyor...</div>;
  const save = async () => {
    try { await http.put("/settings/login", { ...v, logo_path: v.logo_path || null, bg_path: v.bg_path || null }); toast.success("Kaydedildi"); refreshBrand(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  const tile = (active) => `relative h-24 rounded-xl overflow-hidden border-2 transition-colors ${active ? "border-blue-500" : "border-slate-800 hover:border-slate-600"}`;
  return (
    <div className="space-y-5 max-w-3xl">
      <DailyMessageCard />
      <Card className="border-slate-800 bg-slate-900/60 glass p-5 space-y-3" data-testid="brand-settings">
        <div className="font-display font-bold">Marka Yazısı</div>
        <p className="text-xs text-slate-400">Menüde ve giriş ekranında logodaki yazı tipiyle görünür.</p>
        <Input value={v.brand_text} onChange={(e) => setV({ ...v, brand_text: e.target.value })} className="bg-slate-950/50 border-slate-700" data-testid="brand-text-input" />
        <div className="rounded-xl bg-black px-5 py-4 text-2xl text-white"><BrandText text={v.brand_text} testId="brand-text-preview" /></div>
      </Card>

      <Card className="border-slate-800 bg-slate-900/60 glass p-5 space-y-4" data-testid="login-settings">
        <div className="font-display font-bold">Giriş Ekranı</div>
        <p className="text-xs text-slate-400">Boş bırakılan yazılar gösterilmez. Hepsi boşsa sadece giriş kutusu görünür.</p>
        <div>
          <Label className="mb-2 block">Logo</Label>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => setV({ ...v, logo_preset: v.logo_preset ? null : "karahan" })} data-testid="logo-preset-karahan"
                    className={`rounded-xl bg-black px-4 py-3 border-2 ${v.logo_preset ? "border-blue-500" : "border-slate-800"}`}>
              <img src="/brand/karahan-logo.png" alt="Karahan" className="h-6" />
            </button>
            <span className="text-xs text-slate-500">veya kendi logonuz:</span>
            {!v.logo_preset && <PhotoField kind="logo" value={v.logo_path} name={v.company_name || "Logo"} onChange={(p) => setV((x) => ({ ...x, logo_path: p }))} />}
          </div>
        </div>
        <div><Label>Şirket Adı</Label><Input value={v.company_name} onChange={(e) => setV({ ...v, company_name: e.target.value })} className="bg-slate-950/50 border-slate-700" data-testid="login-set-company" /></div>
        <div><Label>Başlık</Label><Input value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} className="bg-slate-950/50 border-slate-700" data-testid="login-set-title" /></div>
        <div><Label>Alt Yazı</Label><Textarea rows={3} value={v.subtitle} onChange={(e) => setV({ ...v, subtitle: e.target.value })} className="bg-slate-950/50 border-slate-700" data-testid="login-set-subtitle" /></div>
        <div>
          <Label className="mb-2 block">Arka Plan</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <button type="button" onClick={() => setV({ ...v, bg_type: "none" })} className={`${tile(v.bg_type === "none")} bg-slate-950 text-xs text-slate-400`} data-testid="login-bg-none">Varsayılan</button>
            {BG_PRESETS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setV({ ...v, bg_type: "preset", bg_preset: k })} className={tile(v.bg_type === "preset" && v.bg_preset === k)} data-testid={`login-bg-preset-${k}`}>
                <img src={`/login-bg/${k}.jpg`} alt={l} className="absolute inset-0 h-full w-full object-cover" />
                <span className="absolute bottom-1 left-2 text-[10px] font-semibold text-white drop-shadow">{l}</span>
              </button>
            ))}
            {v.bg_path && (
              <button type="button" onClick={() => setV({ ...v, bg_type: "upload" })} className={tile(v.bg_type === "upload")} data-testid="login-bg-uploaded">
                <UserAvatar path={v.bg_path} name="BG" className="absolute inset-0 h-full w-full" rounded="rounded-none" />
                <span className="absolute bottom-1 left-2 text-[10px] font-semibold text-white drop-shadow">Yüklenen</span>
              </button>
            )}
            <BgUpload onUploaded={(p) => setV((x) => ({ ...x, bg_path: p, bg_type: "upload" }))} />
          </div>
          {v.bg_type !== "none" && (
            <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
              Karartma <input type="range" min="0" max="90" value={v.bg_dim} onChange={(e) => setV({ ...v, bg_dim: Number(e.target.value) })} className="flex-1 accent-blue-500" data-testid="login-bg-dim" />
              <span className="w-10 text-right tabular-nums">%{v.bg_dim}</span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="border-slate-700" onClick={() => window.open("/login?preview=1", "_blank")} data-testid="login-set-preview">Önizle</Button>
          <Button onClick={save} className="bg-blue-600 hover:bg-blue-500" data-testid="login-set-save">Kaydet</Button>
        </div>
      </Card>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  return (
    <div className="space-y-6">
      <header className="animate-fade-up">
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Settings2 className="text-blue-400" /> Ayarlar</h1>
        <p className="text-slate-400 text-sm mt-1">Tema ve giriş ekranı ayarları</p>
      </header>
      <Tabs defaultValue="theme">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="theme" data-testid="settings-tab-theme"><Palette size={14} className="mr-1.5" /> Tema</TabsTrigger>
          {isAdmin(user) && <TabsTrigger value="login" data-testid="settings-tab-login"><LogIn size={14} className="mr-1.5" /> Marka & Giriş</TabsTrigger>}
        </TabsList>
        <TabsContent value="theme" className="mt-4"><ThemeTab /></TabsContent>
        {isAdmin(user) && <TabsContent value="login" className="mt-4"><LoginTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
